/**
 * The flavour-database reference routes (`/v1/flavour`), as the wheel reads them.
 * Kept apart from the screen so the development chat can draw the same wheel
 * from the same calls.
 */

import {
  flavourNameKey,
  type FlavourCocktail,
  type FlavourCompanion,
  type FlavourPairing,
  type FlavourProfile,
  type FlavourSynergy,
  type Ingredient,
} from "@baros/core";
import { request } from "../api.js";

/** The API's own cap on names per profiles call. */
export const PROFILES_PER_CALL = 20;

export interface FlavourIngredientInfo {
  readonly profile: FlavourProfile | null;
  readonly pairings: readonly FlavourPairing[];
  readonly companions: readonly FlavourCompanion[];
  readonly cocktailCount: number;
}

export interface CocktailPage {
  readonly total: number;
  readonly cocktails: readonly FlavourCocktail[];
}

/** As `/v1/flavour/synergy` answers; `off` while live fetching is switched off. */
export type SynergyResult =
  | { readonly status: "ok"; readonly synergy: FlavourSynergy; readonly cached: boolean }
  | { readonly status: "off" }
  | { readonly status: "unavailable"; readonly reason: string };

const names = (list: readonly string[]) => encodeURIComponent(JSON.stringify(list));

export const flavourApi = {
  /** Every searchable name, about 170 KB: fetch once per screen. */
  names: (token: string) => request<{ names: readonly string[] }>(token, "GET", "/v1/flavour/names"),

  /** Profiles for any number of names, in calls of at most 20. Unknown names are absent. */
  async profiles(token: string, list: readonly string[]): Promise<FlavourProfile[]> {
    const chunks: string[][] = [];
    for (let i = 0; i < list.length; i += PROFILES_PER_CALL) chunks.push(list.slice(i, i + PROFILES_PER_CALL));
    const pages = await Promise.all(
      chunks.map((chunk) =>
        request<{ profiles: readonly FlavourProfile[] }>(token, "GET", `/v1/flavour/profiles?names=${names(chunk)}`),
      ),
    );
    return pages.flatMap((p) => p.profiles);
  },

  ingredient: (token: string, name: string) =>
    request<FlavourIngredientInfo>(token, "GET", `/v1/flavour/ingredients/${encodeURIComponent(name)}`),

  cocktails: (token: string, ingredients: readonly string[], limit: number, offset: number) =>
    request<CocktailPage>(
      token,
      "GET",
      `/v1/flavour/cocktails?ingredients=${names(ingredients)}&limit=${limit}&offset=${offset}`,
    ),

  synergy: (token: string, ingredients: readonly string[]) =>
    request<SynergyResult>(token, "GET", `/v1/flavour/synergy?ingredients=${names(ingredients)}`),

  /** Confirm a link (with any overrides), or clear it with null. Needs `ingredient:edit`. */
  link: (token: string, ingredientId: string, flavour: { ref: string; overrides?: Readonly<Record<string, number>> } | null) =>
    request<{ ingredient: Ingredient }>(token, "PUT", `/v1/ingredients/${encodeURIComponent(ingredientId)}/flavour`, {
      flavour,
    }),
};

/**
 * Names matching what was typed, as the reference wheel's search orders them: those
 * starting with it first, then those containing it anywhere, each group in
 * list order (the doc did not confirm the reference wheel's tie-break within a group).
 */
export function searchFlavourNames(all: readonly string[], typed: string, limit = 20): string[] {
  const q = flavourNameKey(typed);
  if (!q) return [];
  const starts: string[] = [];
  const contains: string[] = [];
  for (const name of all) {
    const k = flavourNameKey(name);
    if (k.startsWith(q)) starts.push(name);
    else if (k.includes(q)) contains.push(name);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}
